<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVenueGeocode } from 'dashboard/composables/useVenueGeocode';
import Icon from 'next/icon/Icon.vue';
import SectionCard from 'dashboard/components-next/section-card/SectionCard.vue';
import VenueLocationPicker from './VenueLocationPicker.vue';
import VenueBuzzCard from './VenueBuzzCard.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { geocodeLocation } = useVenueGeocode();
const isBuzzEnabled = computed(() => isCloudFeatureEnabled(FEATURE_FLAGS.BUZZ));

const loading = ref(true);
const saving = ref(false);
const showDeleteModal = ref(false);
const editingName = ref(false);
const tempName = ref('');
const latitude = ref(null);
const longitude = ref(null);
const bounds = ref(null);
const coordsSeeded = ref(false);

const venueId = computed(() => route.params.venueId);
const venue = computed(() =>
  store.getters['venues/getVenues'].find(v => v.id === venueId.value)
);

const spacesAtVenue = computed(() =>
  (store.getters['spaces/getSpaces'] || []).filter(
    s => s.venue_id === venueId.value
  )
);

const mapZoom = computed(() => {
  if (venue.value?.address) return 15;
  if (venue.value?.city) return 12;
  if (venue.value?.country) return 5;
  return 15;
});

const mapZoomBias = computed(() => {
  if (venue.value?.address) return -2;
  if (venue.value?.city || venue.value?.country) return 2;
  return 0;
});

const mapZoomLevel = computed(() => {
  if (venue.value?.address) return 'address';
  if (venue.value?.city) return 'city';
  if (venue.value?.country) return 'country';
  return null;
});

const seedCoordsFromVenue = () => {
  if (!venue.value || coordsSeeded.value) return;
  latitude.value =
    venue.value.latitude != null ? Number(venue.value.latitude) : null;
  longitude.value =
    venue.value.longitude != null ? Number(venue.value.longitude) : null;
  coordsSeeded.value = true;
};

watch(venue, seedCoordsFromVenue, { immediate: true });

const fetchData = async () => {
  loading.value = true;
  try {
    await Promise.all([
      store.dispatch('venues/get'),
      store.dispatch('spaces/get'),
    ]);
    if (!venue.value) {
      router.push({ name: 'settings_spaces_index', query: { tab: 'venues' } });
      return;
    }
    seedCoordsFromVenue();
  } finally {
    loading.value = false;
  }
};

onMounted(fetchData);

const handleBack = () => {
  router.push({ name: 'settings_spaces_index', query: { tab: 'venues' } });
};

const updateVenue = async payload => {
  if (!venue.value) return;
  try {
    saving.value = true;
    await store.dispatch('venues/update', { id: venue.value.id, ...payload });
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.message ||
        t('SETTINGS.SPACES.VENUES.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const startEditingName = () => {
  tempName.value = venue.value?.name || '';
  editingName.value = true;
};

const saveVenueName = async () => {
  if (!tempName.value.trim()) return;
  await updateVenue({ name: tempName.value.trim() });
  editingName.value = false;
};

const cancelEditingName = () => {
  editingName.value = false;
  tempName.value = '';
};

const updateField = (field, value) => updateVenue({ [field]: value });

const updateMapLatitude = value => {
  latitude.value = value;
  bounds.value = null;
  updateField('latitude', value);
};

const updateMapLongitude = value => {
  longitude.value = value;
  bounds.value = null;
  updateField('longitude', value);
};

let locationUpdateChain = Promise.resolve();

const updateLocationField = (field, value) => {
  const trimmed = value?.trim() || null;

  const task = async () => {
    const parts = {
      address: field === 'address' ? trimmed : venue.value?.address,
      city: field === 'city' ? trimmed : venue.value?.city,
      country: field === 'country' ? trimmed : venue.value?.country,
    };

    const payload = { [field]: trimmed };
    try {
      const coords = await geocodeLocation(parts);
      if (coords) {
        latitude.value = coords.latitude;
        longitude.value = coords.longitude;
        bounds.value = coords.bounds;
        payload.latitude = coords.latitude;
        payload.longitude = coords.longitude;
      }
    } catch (error) {
      if (error?.code !== 'GEOCODE_CANCELED') {
        useAlert(t('SETTINGS.SPACES.VENUES.GEOCODE_ERROR'));
      }
    }

    await updateVenue(payload);
  };

  locationUpdateChain = locationUpdateChain.then(task, task);
  return locationUpdateChain;
};

const updateSocial = (key, value) => {
  const socialProfiles = {
    ...(venue.value?.social_profiles || {}),
    [key]: value?.trim() || null,
  };
  updateVenue({ social_profiles: socialProfiles });
};

const handleDelete = () => {
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  try {
    await store.dispatch('venues/delete', venueId.value);
    router.push({ name: 'settings_spaces_index', query: { tab: 'venues' } });
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.message ||
        t('SETTINGS.SPACES.VENUES.DELETE_ERROR')
    );
  }
};

const navigateToSpace = spaceId => {
  router.push({ name: 'settings_spaces_show', params: { spaceId } });
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-white dark:bg-slate-900">
    <div
      v-if="!loading && venue"
      class="flex flex-col h-full bg-n-surface-1 overflow-hidden"
    >
      <div
        class="py-5 flex items-center justify-between bg-n-surface-1 shrink-0"
      >
        <div
          class="max-w-7xl w-full mx-auto px-6 flex items-center justify-between"
        >
          <div class="flex items-center gap-4">
            <button
              class="flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
              @click="handleBack"
            >
              <Icon icon="i-lucide-undo-2" class="size-4 mr-2" />
              {{ t('SETTINGS.SPACES.ALL_VENUES') }}
            </button>
            <div v-if="editingName" class="flex items-center gap-2">
              <input
                v-model="tempName"
                type="text"
                class="reset-base no-margin text-xl font-bold text-slate-900 dark:text-white bg-transparent border-b-2 border-purple-500 focus:outline-none min-w-[300px] px-2 py-1"
                autofocus
                @keyup.enter="saveVenueName"
                @keyup.escape="cancelEditingName"
              />
              <button
                class="p-1 text-purple-600 hover:text-purple-700"
                :disabled="saving"
                @click="saveVenueName"
              >
                <Icon icon="i-lucide-check" class="size-5" />
              </button>
              <button
                class="p-1 text-slate-400 hover:text-slate-600"
                @click="cancelEditingName"
              >
                <Icon icon="i-lucide-x" class="size-5" />
              </button>
            </div>
            <h1
              v-else
              class="text-xl font-bold text-slate-900 dark:text-white cursor-pointer hover:text-purple-600 transition-colors"
              :title="t('SETTINGS.SPACES.CLICK_TO_EDIT')"
              @click="startEditingName"
            >
              {{ venue.name }}
            </h1>
          </div>
          <button
            class="p-2 text-slate-400 hover:text-white hover:bg-red-600 rounded-lg transition-colors flex h-10 w-10 justify-center items-center"
            @click="handleDelete"
          >
            <Icon icon="i-lucide-trash-2" class="size-5" />
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-hidden">
        <div class="h-full overflow-y-auto">
          <div class="max-w-7xl mx-auto px-6 py-8 space-y-8">
            <SectionCard
              v-if="isBuzzEnabled"
              :title="t('SETTINGS.SPACES.VENUES.IDENTITY.TITLE')"
              icon="i-lucide-globe"
            >
              <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {{ t('SETTINGS.SPACES.VENUES.IDENTITY.DESCRIPTION') }}
              </p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('SETTINGS.SPACES.VENUES.WEBSITE') }}
                  </label>
                  <input
                    :value="venue.website || ''"
                    type="text"
                    class="w-full"
                    :placeholder="
                      t('SETTINGS.SPACES.VENUES.WEBSITE_PLACEHOLDER')
                    "
                    :disabled="saving"
                    @change="
                      updateField('website', $event.target.value || null)
                    "
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('SETTINGS.SPACES.VENUES.GOOGLE_PLACE_ID') }}
                  </label>
                  <input
                    :value="venue.google_place_id || ''"
                    type="text"
                    class="w-full"
                    :placeholder="
                      t('SETTINGS.SPACES.VENUES.GOOGLE_PLACE_ID_PLACEHOLDER')
                    "
                    :disabled="saving"
                    @change="
                      updateField(
                        'google_place_id',
                        $event.target.value || null
                      )
                    "
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('SETTINGS.SPACES.VENUES.SOCIAL_INSTAGRAM') }}
                  </label>
                  <input
                    :value="venue.social_profiles?.instagram || ''"
                    type="text"
                    class="w-full"
                    :disabled="saving"
                    @change="updateSocial('instagram', $event.target.value)"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('SETTINGS.SPACES.VENUES.SOCIAL_FACEBOOK') }}
                  </label>
                  <input
                    :value="venue.social_profiles?.facebook || ''"
                    type="text"
                    class="w-full"
                    :disabled="saving"
                    @change="updateSocial('facebook', $event.target.value)"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('SETTINGS.SPACES.VENUES.SOCIAL_LINKEDIN') }}
                  </label>
                  <input
                    :value="venue.social_profiles?.linkedin || ''"
                    type="text"
                    class="w-full"
                    :disabled="saving"
                    @change="updateSocial('linkedin', $event.target.value)"
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard
              :title="t('SETTINGS.SPACES.VENUES.LOCATION.TITLE')"
              icon="i-lucide-map-pin"
            >
              <div class="space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-n-slate-12 mb-1">
                      {{ t('SETTINGS.SPACES.VENUES.COUNTRY') }}
                    </label>
                    <input
                      :value="venue.country || ''"
                      type="text"
                      class="w-full"
                      :placeholder="
                        t('SETTINGS.SPACES.VENUES.COUNTRY_PLACEHOLDER')
                      "
                      @change="
                        updateLocationField('country', $event.target.value)
                      "
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-n-slate-12 mb-1">
                      {{ t('SETTINGS.SPACES.VENUES.CITY') }}
                    </label>
                    <input
                      :value="venue.city || ''"
                      type="text"
                      class="w-full"
                      :placeholder="
                        t('SETTINGS.SPACES.VENUES.CITY_PLACEHOLDER')
                      "
                      @change="
                        updateLocationField('city', $event.target.value)
                      "
                    />
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('SETTINGS.SPACES.VENUES.ADDRESS') }}
                  </label>
                  <input
                    :value="venue.address || ''"
                    type="text"
                    class="w-full"
                    :placeholder="
                      t('SETTINGS.SPACES.VENUES.ADDRESS_PLACEHOLDER')
                    "
                    @change="
                      updateLocationField('address', $event.target.value)
                    "
                  />
                </div>
                <div
                  v-if="
                    venue.address ||
                      venue.city ||
                      venue.country ||
                      latitude != null
                  "
                >
                  <p class="text-xs text-n-slate-10 mb-2">
                    {{ t('SETTINGS.SPACES.VENUES.LOCATION_HINT') }}
                  </p>
                  <VenueLocationPicker
                    :latitude="latitude"
                    :longitude="longitude"
                    :bounds="bounds"
                    :zoom="mapZoom"
                    :zoom-bias="mapZoomBias"
                    :zoom-level="mapZoomLevel"
                    @update:latitude="updateMapLatitude"
                    @update:longitude="updateMapLongitude"
                  />
                </div>
                <div class="max-w-xs">
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('SETTINGS.SPACES.VENUES.RADIUS_KM') }}
                  </label>
                  <input
                    :value="venue.radius_km ?? ''"
                    type="number"
                    class="w-full"
                    min="1"
                    max="50"
                    :placeholder="
                      t('SETTINGS.SPACES.VENUES.RADIUS_KM_PLACEHOLDER')
                    "
                    @change="
                      updateField(
                        'radius_km',
                        $event.target.value
                          ? parseInt($event.target.value, 10)
                          : null
                      )
                    "
                  />
                </div>
              </div>
            </SectionCard>

            <VenueBuzzCard v-if="isBuzzEnabled" :venue="venue" />

            <SectionCard
              :title="t('SETTINGS.SPACES.VENUES.SPACES_AT_VENUE.TITLE')"
              icon="i-lucide-orbit"
            >
              <p
                v-if="spacesAtVenue.length === 0"
                class="text-sm text-slate-500 dark:text-slate-400"
              >
                {{ t('SETTINGS.SPACES.VENUES.NO_SPACES') }}
              </p>
              <div v-else class="flex flex-wrap gap-2">
                <button
                  v-for="space in spacesAtVenue"
                  :key="space.id"
                  type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-n-weak text-sm text-woot-600 hover:bg-n-alpha-2 transition-colors"
                  @click="navigateToSpace(space.id)"
                >
                  <Icon icon="i-lucide-orbit" class="size-4" />
                  {{ space.name }}
                </button>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>

      <div
        v-if="showDeleteModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
        @click.self="showDeleteModal = false"
      >
        <div
          class="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4"
        >
          <h3
            class="text-lg font-semibold text-center text-slate-900 dark:text-white mb-2"
          >
            {{ t('SETTINGS.SPACES.VENUES.DELETE_CONFIRM_TITLE') }}
          </h3>
          <p
            class="text-sm text-center text-slate-500 dark:text-slate-400 mb-6"
          >
            {{ t('SETTINGS.SPACES.VENUES.DELETE_CONFIRM_MESSAGE') }}
          </p>
          <div class="grid grid-cols-2 gap-3">
            <button
              class="px-4 py-2 text-sm font-medium rounded-lg border border-slate-300"
              @click="showDeleteModal = false"
            >
              {{ t('SETTINGS.SPACES.VENUES.DELETE_CONFIRM_NO') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              @click="confirmDelete"
            >
              {{ t('SETTINGS.SPACES.VENUES.DELETE_CONFIRM_YES') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="flex items-center justify-center h-full bg-white dark:bg-slate-900"
    >
      <div class="flex flex-col items-center">
        <div
          class="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-3"
        />
        <p class="text-sm text-slate-500">
          {{ t('SETTINGS.SPACES.VENUES.LOADING') }}
        </p>
      </div>
    </div>
  </div>
</template>
